import axios, { AxiosResponse } from 'axios';
import { environment } from '../../environment';
import { useAtom } from 'jotai';
import { errorCatcher, useSnackbar } from '../../hooks';
import { useLogin } from '../login';
import { ITranslationLanguage, translationLanguageAtom } from './types';


type SetTranslationType = (payload: ITranslationLanguage[]) => void;
type FetchTranslationTypes = () => void;
type SaveTranslationType = (payload: ITranslationLanguage) => void;
type DeleteTranslationType = (language_id: number) => Promise<boolean>;
type UpdateTranslationType = (payload: ITranslationLanguage) => void;

type TranslationLanguageKeyFunctions = {
    get: FetchTranslationTypes;
    add: SaveTranslationType;
    delete: DeleteTranslationType;
    update: UpdateTranslationType;
}

type UseTranslationLanguages = [
    ITranslationLanguage[], 
    SetTranslationType,
    TranslationLanguageKeyFunctions
];

export const useTranslationLanguages = (): UseTranslationLanguages => {
    const [translationLanguages, setTranslationLanguages] = useAtom(translationLanguageAtom);

    const [,setSnackbar] = useSnackbar();
    const [,authToken, loginFunctions] = useLogin();

    const getTranslationLanguages = async (setTranslationLanguages: SetTranslationType): Promise<void> => {
        const languages: AxiosResponse<ITranslationLanguage[]> | void = await axios.get(environment.languagesUrl, {
            headers: {
                'X-Wallmates-Auth': authToken
            }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));

        if (languages) {
            setTranslationLanguages(languages.data);
        } else {
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: 'There was and issue getting translation languages. Please try again later.'
            });
        }
    }
    
    const saveTranslationLanguage = async (translationLanguage: ITranslationLanguage): Promise<ITranslationLanguage | null> => {
        const language: AxiosResponse<ITranslationLanguage> | void = await axios.post(environment.languagesUrl, {
            language: translationLanguage
        }, {
            headers: {
                'X-Wallmates-Auth': authToken
            }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));

        if (language) {
            return language.data;
        } else {
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: 'There was and issue creating a translation language. Please try again later.'
            });
            return null;
        }
    }
    
    const deleteTranslationLanguage = async (language_id: number): Promise<boolean> => {
        const deleteResponse: AxiosResponse<boolean> | void = await axios.delete(`${environment.languagesUrl}?language_id=${language_id}`, {
            headers: {
                'X-Wallmates-Auth': authToken
            }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));

        if (deleteResponse) {
            return deleteResponse.data;
        } else {
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: 'There was and issue deleting a translation language. Please try again later.'
            });
            return false;
        }
    }
    
    const updateTranslationLanguage = async (translationLanguage: ITranslationLanguage): Promise<ITranslationLanguage | null> => {
        const language: AxiosResponse<ITranslationLanguage> | void = await axios.put(environment.languagesUrl, {
            language: translationLanguage
        }, {
            headers: {
                'X-Wallmates-Auth': authToken
            }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));
        
        if (language) {
            return language.data;
        } else {
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: 'There was and issue updating a translation language. Please try again later.'
            });
            return null;
        }
    }

    const fetchTranslations = () => {
        getTranslationLanguages(setTranslationLanguages);
    }

    const saveLanguage = async (language: ITranslationLanguage): Promise<void> => {
        const value = await saveTranslationLanguage(language);
        if (value) {
            setTranslationLanguages([...translationLanguages, value]);
        }
    }

    const deleteLanguage = async (language_id: number): Promise<boolean> => {
        const result = await deleteTranslationLanguage(language_id);
        setTranslationLanguages(translationLanguages.filter((item: ITranslationLanguage) => item.id !== language_id))
        return result;
    }

    const updateLanguage = async (language: ITranslationLanguage): Promise<void> => {
        await updateTranslationLanguage(language);
        setTranslationLanguages(
            translationLanguages.map((item: ITranslationLanguage) => {
                if (item.id === language.id) return language;
                return item;
            })
        )
    }

    const keyFunctions: TranslationLanguageKeyFunctions = {
        add: saveLanguage,
        delete: deleteLanguage,
        get: fetchTranslations,
        update: updateLanguage
    }

    return [
        translationLanguages, 
        setTranslationLanguages, 
        keyFunctions
    ];
}